"use client";

import { memo, useEffect, useMemo, useRef, type JSX } from "react";
import {
  loadAdvancedSearchQueryActions,
  loadPaginationActions,
} from "@coveo/headless";
import { CoveoFieldName } from "../../../utils/coveoUtils";
import { useSearchEngine } from "../../../hooks/useSearchEngine";
import { type CustomEnumOption } from "types/Coveo";
import { type TaxonomyItem } from "types/Search/TaxonomyItem";
import {
  type SearchFilterFields,
  type SearchFilterProps,
} from "./search-filter.props";

const NAME_SEPARATOR = "|";

const joinTaxonomyLabels = (items?: TaxonomyItem[]): string =>
  (items ?? [])
    .map((item) => item?.fields?.TaxonomyLabel?.value)
    .filter(Boolean)
    .join(NAME_SEPARATOR);

const joinCustomEnumValues = (items?: CustomEnumOption[]): string =>
  (items ?? [])
    .map((item) => item?.fields?.Value?.value)
    .filter(Boolean)
    .join(NAME_SEPARATOR);

const buildExpression = (names: string): string | undefined => {
  if (!names) return undefined;
  const parts = names
    .split(NAME_SEPARATOR)
    .filter(Boolean)
    .map((n) => `"${n}"`)
    .join(",");
  return parts ? `(${parts})` : undefined;
};

interface NamedFilterSet {
  templates: string;
  markets: string;
  applications: string;
  solutions: string;
  industries: string;
  articleTypes: string;
  articleTopics: string;
  articleAuthors: string;
  newsletters: string;
  newsTypes: string;
  newsTopics: string;
  storyTypes: string;
  storyTopics: string;
  eventTypes: string;
  locationTypes: string;
  locationRegions: string;
  companies: string;
  offeringTypes: string;
  translationOwners: string;
  regions: string;
  countries: string;
  googleAnalyticsRepGroups: string;
}

const collectFilterNames = (fields: SearchFilterFields): NamedFilterSet => ({
  templates: joinCustomEnumValues(fields?.TemplateTypes),
  markets: joinTaxonomyLabels(fields?.Markets),
  applications: joinTaxonomyLabels(fields?.Applications),
  solutions: joinTaxonomyLabels(fields?.Solutions),
  industries: joinTaxonomyLabels(fields?.Industries),
  articleTypes: joinTaxonomyLabels(fields?.BlogArticleType),
  articleTopics: joinTaxonomyLabels(fields?.BlogArticleTopic),
  articleAuthors: joinTaxonomyLabels(fields?.BlogArticleAuthor),
  newsletters: joinTaxonomyLabels(fields?.NewsLetterName),
  newsTypes: joinTaxonomyLabels(fields?.NewsType),
  newsTopics: joinTaxonomyLabels(fields?.NewsTopic),
  storyTypes: joinTaxonomyLabels(fields?.StoryType),
  storyTopics: joinTaxonomyLabels(fields?.StoryTopic),
  eventTypes: joinCustomEnumValues(fields?.EventsType),
  locationTypes: joinTaxonomyLabels(fields?.LocationType),
  locationRegions: joinTaxonomyLabels(fields?.LocationRegion),
  companies: joinTaxonomyLabels(fields?.Companies),
  offeringTypes: joinTaxonomyLabels(fields?.OfferingType),
  translationOwners: joinTaxonomyLabels(fields?.TranslationOwner),
  regions: joinTaxonomyLabels(fields?.Regions),
  countries: joinTaxonomyLabels(fields?.Countries),
  googleAnalyticsRepGroups: joinTaxonomyLabels(fields?.GoogleAnalyticsRepGroup),
});

const buildAdvancedQueryParts = (
  names: NamedFilterSet,
  fields: SearchFilterFields,
): { aq?: string; cq?: string; dq?: string; lq?: string } => {
  if (!process.env.NEXT_PUBLIC_COVEO_FILTERS_ENABLED) {
    return {
      aq: fields?.aq?.value,
      cq: fields?.cq?.value,
      dq: fields?.dq?.value,
      lq: fields?.lq?.value,
    };
  }

  const queryPartsCq: string[] = [];
  const mappings: Array<[string, string]> = [
    [names.templates, CoveoFieldName.PageTemplateName],
    [names.markets, CoveoFieldName.TaxonomyMarkets],
    [names.applications, CoveoFieldName.TaxonomyApplications],
    [names.solutions, CoveoFieldName.TaxonomySolutions],
    [names.industries, CoveoFieldName.TaxonomyIndustries],
    [names.articleTypes, CoveoFieldName.TaxonomyArticleTypes],
    [names.articleTopics, CoveoFieldName.TaxonomyArticleTopics],
    [names.articleAuthors, CoveoFieldName.ArticleAuthor],
    [names.newsletters, CoveoFieldName.TaxonomyNewsLetterNames],
    [names.newsTypes, CoveoFieldName.TaxonomyNewsTypes],
    [names.newsTopics, CoveoFieldName.TaxonomyNewsTopics],
    [names.storyTypes, CoveoFieldName.TaxonomyStoryTypes],
    [names.storyTopics, CoveoFieldName.TaxonomyStoryTopics],
    [names.eventTypes, CoveoFieldName.EventType],
    [names.locationTypes, CoveoFieldName.TaxonomyLocationTypes],
    [names.locationRegions, CoveoFieldName.TaxonomyLocationRegions],
    [names.companies, CoveoFieldName.TaxonomyCompanies],
    [names.translationOwners, CoveoFieldName.TaxonomyTranslationOwner],
    [names.offeringTypes, CoveoFieldName.TaxonomyOfferingType],
    [names.regions, CoveoFieldName.TaxonomyRegions],
    [names.countries, CoveoFieldName.TaxonomyCountries],
    [
      names.googleAnalyticsRepGroups,
      CoveoFieldName.TaxonomyGoogleAnalyticsRepGroup,
    ],
  ];

  for (const [filterNames, coveoField] of mappings) {
    const expr = buildExpression(filterNames);
    if (expr) queryPartsCq.push(`${coveoField}=${expr}`);
  }

  if (fields?.cq?.value) queryPartsCq.push(fields?.cq?.value);

  return {
    cq: queryPartsCq.length > 0 ? queryPartsCq.join(" AND ") : undefined,
    aq: fields?.aq?.value ? fields?.aq?.value : undefined,
    dq: fields?.dq?.value ? fields?.dq?.value : undefined,
    lq: fields?.lq?.value ? fields?.lq?.value : undefined,
  };
};

// Editing-mode metadata hint table — replaces the original 25-block wall of
// repeated `<pre>` elements with a single mapping.
const EDITING_HINT_LABELS: ReadonlyArray<
  readonly [keyof NamedFilterSet | "aq" | "cq" | "dq" | "lq", string]
> = [
  ["templates", "Templates"],
  ["markets", "Markets"],
  ["applications", "Application"],
  ["solutions", "Solution"],
  ["industries", "Industries"],
  ["articleTypes", "Article types"],
  ["articleTopics", "Article topics"],
  ["articleAuthors", "Article authors"],
  ["newsletters", "Newsletter names"],
  ["newsTypes", "News types"],
  ["newsTopics", "News topics"],
  ["storyTypes", "Story Types"],
  ["storyTopics", "Story Topics"],
  ["eventTypes", "Event Types"],
  ["locationTypes", "Location Types"],
  ["locationRegions", "Location Regions"],
  ["companies", "Company Names"],
  ["offeringTypes", "Offering Type"],
  ["translationOwners", "Translation Owner"],
  ["googleAnalyticsRepGroups", "Google Analytics Rep Group"],
  ["countries", "Countries"],
  ["regions", "Regions"],
];

const SearchFilterComponent = (props: SearchFilterProps): JSX.Element => {
  // Wrap so that an undefined `props.fields` doesn't yield a fresh `{}` on
  // every render and invalidate downstream useMemo deps.
  const fields = useMemo<SearchFilterFields>(
    () => props.fields ?? {},
    [props.fields],
  );
  const params = props.params ?? {};
  const { page } = props;
  const isPageEditing = page.mode.isEditing;

  console.log(
    "---->[SearchFilter:Default] Component",
    fields,
    params.SearchScope,
  );

  const searchEngine = useSearchEngine(params.SearchScope);
  const filtersAppliedRef = useRef(false);

  const names = useMemo(() => collectFilterNames(fields), [fields]);

  const advancedQueryParts = useMemo(
    () => buildAdvancedQueryParts(names, fields),
    [names, fields],
  );

  useEffect(() => {
    if (filtersAppliedRef.current) return;
    if (!searchEngine) return;

    const { aq, cq, dq, lq } = advancedQueryParts;

    const itemId = page?.layout?.sitecore?.route?.itemId;
    const pageId = itemId?.toString()?.replaceAll("-", "")?.toUpperCase();

    let additionalPageConditions = "";

    if (pageId) {
      additionalPageConditions = `(NOT ${CoveoFieldName.PageId}=="${pageId}")`;
      if (props.fields?.ChildPages?.value === true) {
        additionalPageConditions = `(NOT ${CoveoFieldName.PageId}=="${pageId}") AND (${CoveoFieldName.PageParentId}=="${pageId}")`;
      }
    }

    let finalCq = additionalPageConditions;
    if (cq && additionalPageConditions) {
      finalCq = `${cq} AND (${additionalPageConditions})`;
    } else if (cq) {
      finalCq = cq;
    }

    if (!aq && !finalCq && !dq && !lq) return;

    const queryCreators = loadAdvancedSearchQueryActions(searchEngine);
    searchEngine.dispatch(
      queryCreators.registerAdvancedSearchQueries({
        cq: finalCq,
        aq,
        dq,
        lq,
      }),
    );

    const paginationCreator = loadPaginationActions(searchEngine);
    const numberOfResults = fields?.NumberOfResults?.value
      ? fields.NumberOfResults.value
      : 8;
    searchEngine.dispatch(
      paginationCreator.registerNumberOfResults(numberOfResults),
    );

    filtersAppliedRef.current = true;
  }, [searchEngine, advancedQueryParts, page, fields?.NumberOfResults?.value]);

  if (!isPageEditing) return <></>;

  const renderHintRow = (
    label: string,
    value: string | undefined,
  ): JSX.Element | null => {
    if (!value) return null;
    return (
      <pre key={label}>
        <strong>{label}: </strong>
        {value.replaceAll(NAME_SEPARATOR, ", ")}
      </pre>
    );
  };

  return (
    <div className={`is-metadata-hint ${params?.styles ?? ""}`}>
      <strong>Search static filter</strong>
      <div>
        {fields?.NumberOfResults?.value && (
          <pre>
            <strong>number of results: </strong>
            {fields.NumberOfResults.value}
          </pre>
        )}
        {EDITING_HINT_LABELS.map(([key, label]) =>
          key in names
            ? renderHintRow(label, names[key as keyof NamedFilterSet])
            : null,
        )}
        {renderHintRow("aq", fields?.aq?.value)}
        {renderHintRow("cq", fields?.cq?.value)}
        {renderHintRow("dq", fields?.dq?.value)}
        {renderHintRow("lq", fields?.lq?.value)}
      </div>
    </div>
  );
};

export const Default = memo(SearchFilterComponent);
